import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { ATELIER, FONTS, ICON, MODES } from '../../tokens';
import { useWorkspace } from '../../WorkspaceContext';
import { useAuth } from '../../../contexts/AuthContext';
import { BentoTile } from './BentoTile';
import mainLogo from '../../../assets/DAGGPT-01.png';

function ChapterLabel({ num, text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ 
        fontFamily: FONTS.mono, fontSize: 10, letterSpacing: '0.1em',
        color: ATELIER.accent, fontWeight: 700 
      }}>{num}</span>
      <span style={{ 
        width: 12, height: 1, background: ATELIER.borderSoft 
      }} />
      <span style={{
        fontFamily: FONTS.mono, fontSize: 10, letterSpacing: '0.18em',
        color: ATELIER.inkMuted, fontWeight: 700, textTransform: 'uppercase'
      }}>{text}</span>
    </div>
  );
}

/* ── Roman numeral year helper (editorial touch) ─────────── */
function romanize(num) {
  const map = [
    ['M', 1000], ['CM', 900], ['D', 500], ['CD', 400],
    ['C', 100], ['XC', 90], ['L', 50], ['XL', 40],
    ['X', 10], ['IX', 9], ['V', 5], ['IV', 4], ['I', 1],
  ];
  let out = ''; let n = num;
  for (const [r, v] of map) { while (n >= v) { out += r; n -= v; } }
  return out;
}

/* Day-of-year calculation (for edition number) */
function dayOfYear(d) {
  const start = new Date(d.getFullYear(), 0, 0);
  const diff = d - start;
  return Math.floor(diff / 86400000);
}

/* ── Storage estimation ────────────────────────────────────
   Estimates per-output payload size (in MB) per modality,
   so we can show a meaningful "storage used" stat without
   needing real CDN telemetry yet. */
const STORAGE_MB_PER_OUTPUT = {
  image: 2.4,
  video: 78.0,
  music: 4.8,
  chat:  0.012,
};
const STORAGE_QUOTA_MB = 10 * 1024; // 10 GB plan
const STORAGE_TYPE_META = {
  image: { label: 'Images',  accent: '#8B5CF6' },
  video: { label: 'Video',   accent: '#EC4899' },
  music: { label: 'Music',   accent: '#06B6D4' },
  chat:  { label: 'Threads', accent: '#7C3AED' },
};

function computeStorage(turns) {
  const byType = { image: 0, video: 0, music: 0, chat: 0 };
  let monthMB = 0;
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  for (const t of turns || []) {
    if (t.role !== 'assistant' || t.status !== 'done') continue;
    const type = t.type || 'chat';
    const mb = STORAGE_MB_PER_OUTPUT[type] || 0.05;
    byType[type] = (byType[type] || 0) + mb;
    if ((t.createdAt || 0) >= monthStart) monthMB += mb;
  }
  const total = Object.values(byType).reduce((a, b) => a + b, 0);
  return { byType, total, monthMB };
}

function formatStorage(mb) {
  if (mb >= 1024) return { value: (mb / 1024).toFixed(2), unit: 'GB' };
  if (mb >= 1)    return { value: mb.toFixed(1),         unit: 'MB' };
  if (mb > 0)     return { value: (mb * 1024).toFixed(0), unit: 'KB' };
  return { value: '0', unit: 'KB' };
}

/* ISO week number */
function weekOfYear(d) {
  const target = new Date(d.valueOf());
  const dayNr = (d.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  if (target.getDay() !== 4) {
    target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
  }
  return 1 + Math.ceil((firstThursday - target) / 604800000);
}

/**
 * BentoShell - the premium Bento landing.
 *
 * Composition (12-col grid, 20px gap, 28px page padding):
 *
 *   ┌────────────────── GREETING HERO (12 cols) ──────────────────┐
 *   │  Good afternoon, Vinod.                                       │
 *   │  What shall we *make* today?  [serif huge]                    │
 *   └───────────────────────────────────────────────────────────────┘
 *
 *   ┌── Latest work (6�-2) ───┬── Memory (3�-2) ──┬── Model (3�-2) ──┐
 *   │                         │                   │                   │
 *   └─────────────────────────┴───────────────────┴───────────────────┘
 *
 *   ┌──────────────── PROJECTS (12 cols, wide strip) ──────────────┐
 *   └──────────────────────────────────────────────────────────────┘
 *
 *   ┌────── PASSPORT (dark, 12 cols, horizontal) ──────────────────┐
 *   └──────────────────────────────────────────────────────────────┘
 *
 * The composer is NOT in the grid anymore - it's a FloatingComposer rendered
 * by ThreadView at the bottom of the main column. The grid is purely
 * informational / dashboard.
 */
export function BentoShell() {
  const { projects, conversation, conversations } = useWorkspace();
  const { userProfile, user, dgccBalance } = useAuth() || {};

  const [isCompact, setIsCompact] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      setIsCompact(window.innerWidth < 1440 || window.innerHeight < 850);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { allTurns, visuals } = useMemo(() => {
    const _turns = [];
    if (conversation?.turns) _turns.push(...conversation.turns);
    for (const c of conversations || []) {
      if (c.turns) _turns.push(...c.turns);
    }
    for (const p of projects || []) {
      for (const c of p.conversations || []) {
        for (const t of c.turns || []) _turns.push(t);
      }
    }
    _turns.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    const _visuals = _turns.filter(t =>
      t.role === 'assistant' &&
      ((t.type === 'image' && t.imageUrl) || 
       (t.type === 'video' && (t.videoUrl || t.videoThumb)) ||
       (t.type === 'music' && (t.audioUrl || t.meta?.sunoTracks?.length > 0)))
    ).slice(0, 30);
    return { allTurns: _turns, visuals: _visuals };
  }, [projects, conversation, conversations]);

  const firstName =
    (userProfile?.name || user?.email || 'there').split(/[\s@.]/)[0];
  const capitalized = firstName.charAt(0).toUpperCase() + firstName.slice(1);
  const hour = new Date().getHours();
  const greeting =
    hour < 5  ? 'Still up' :
    hour < 12 ? 'Good morning' :
    hour < 17 ? 'Good afternoon' :
    hour < 22 ? 'Good evening' : 'Late night';

  const now = new Date();
  const dateLong = now.toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
  });
  const dateShort = now.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric',
  });
  const edition = String(dayOfYear(now)).padStart(3, '0');
  const week = String(weekOfYear(now)).padStart(2, '0');
  const romanYear = romanize(now.getFullYear());

  return (
    <div className="dgw-scroll bento-shell-root" style={{
      flex: 1,
      overflowY: 'hidden',
      background: ATELIER.bgSubtle,
      fontFamily: FONTS.sans,
      position: 'relative',
    }}>
      {/* Subtle paper-grain noise overlay across the whole canvas */}
      <div aria-hidden style={{
        position: 'absolute', inset: 0,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3CfeColorMatrix values='0 0 0 0 0.48 0 0 0 0 0.23 0 0 0 0 0.93 0 0 0 0.65 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.35'/%3E%3C/svg%3E")`,
        opacity: 0.035,
        pointerEvents: 'none',
        mixBlendMode: 'multiply',
        zIndex: 0,
      }} />

      <style>{`
        .bento-shell-root {
          overflow-y: hidden !important;
        }
        @media (max-width: 1024px) {
          .bento-shell-root {
            overflow-y: auto !important;
            padding-bottom: 24px;
          }
          .bento-live-chip {
            position: relative !important;
            top: auto !important;
            right: auto !important;
            margin-bottom: 16px !important;
            display: inline-flex !important;
          }
          .bento-hero-heading {
            font-size: clamp(34px, 8vw, 40px) !important;
          }
          .bento-modality-strip {
            flex-wrap: wrap !important;
          }
          .bento-modality-strip-text {
            display: none !important;
          }
          .bento-tile-grid {
            display: flex !important;
            flex-direction: column !important;
            gap: 16px !important;
          }
          .bento-tile-grid > div {
            min-height: 180px;
          }
        }
      `}</style>

      <div style={{
        maxWidth: 1400,
        margin: '0 auto',
        padding: isCompact ? '12px 16px 8px' : '28px 28px 24px',
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Editorial folio bar - DATE */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
            marginBottom: isCompact ? 6 : 16, paddingRight: 2,
          }}
        >
            <span style={{
              fontFamily: FONTS.mono, fontSize: 10.5, letterSpacing: '0.24em',
              fontWeight: 700, color: ATELIER.inkMuted, textTransform: 'uppercase',
            }}>{dateLong}</span>
        </motion.div>

        {/* GREETING HERO - full width, editorial spread */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
          style={{
            position: 'relative',
            padding: isCompact ? '16px 24px 14px' : '36px 40px 28px',
            marginBottom: isCompact ? 12 : 22,
            borderRadius: 24,
            background:
              'radial-gradient(1400px 340px at 0% 0%, rgba(167,139,250,0.12) 0%, transparent 55%), ' +
              'radial-gradient(1000px 300px at 100% 100%, rgba(99,102,241,0.08) 0%, transparent 55%), ' +
              '#FFFFFF',
            border: `1px solid ${ATELIER.border}`,
            overflow: 'hidden',
          }}
        >
          {/* Corner register marks (Swiss print tradition) */}
          <RegisterMark position="tl" />
          <RegisterMark position="tr" />
          <RegisterMark position="bl" />
          <RegisterMark position="br" />

          {/* Removed watermark logo */}

          {/* Live chip top-right */}
          <div className="bento-live-chip" aria-hidden style={{
            position: 'absolute', top: isCompact ? 14 : 22, right: 28,
            display: 'flex', alignItems: 'center', gap: 7,
            padding: '5px 10px',
            background: ATELIER.accentSoft,
            border: `1px solid ${ATELIER.borderAccent}`,
            borderRadius: 999,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: 3,
              background: ATELIER.ok,
              boxShadow: `0 0 8px ${ATELIER.ok}`,
              animation: 'dgwDotBlink 2.4s ease-in-out infinite',
            }} />
            <span style={{
              fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.24em',
              fontWeight: 700, color: ATELIER.accent, textTransform: 'uppercase',
            }}>Live Session</span>
          </div>

          {/* Eyebrow */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            marginBottom: isCompact ? 6 : 14, position: 'relative',
          }}>
            <span style={{
              fontFamily: FONTS.mono, fontSize: 10.5, letterSpacing: '0.26em',
              fontWeight: 700, color: ATELIER.inkMuted,
              textTransform: 'uppercase',
            }}>
              {greeting}, {capitalized}.
            </span>
          </div>

          {/* Headline */}
          <h1 className="bento-hero-heading" style={{
            fontFamily: FONTS.serif,
            fontSize: isCompact ? 'clamp(32px, 3.8vw, 48px)' : 'clamp(40px, 5.4vw, 76px)',
            fontWeight: 400,
            lineHeight: 1.02,
            letterSpacing: '-0.034em',
            color: ATELIER.ink,
            margin: 0,
            maxWidth: 1100,
            position: 'relative',
          }}>
            What shall we&nbsp;
            <span style={{ fontStyle: 'italic', color: ATELIER.accent }}>make</span>
            &nbsp;today?
          </h1>

          {/* Tagline with rule */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14,
            marginTop: isCompact ? 10 : 20, position: 'relative',
          }}>
            <span style={{
              fontFamily: FONTS.serif, fontStyle: 'italic',
              fontSize: isCompact ? 14 : 16, color: ATELIER.inkMid,
              letterSpacing: '-0.005em', fontWeight: 400,
            }}>
              One platform. Infinite intelligence.
            </span>
            <span style={{ flex: 1, maxWidth: 180, height: 1, background: ATELIER.borderStrong }} />
          </div>

          <p style={{
            marginTop: isCompact ? 8 : 14,
            fontSize: isCompact ? 12.5 : 13.5, lineHeight: 1.6,
            color: ATELIER.inkSoft,
            letterSpacing: '-0.005em',
            maxWidth: 620,
            position: 'relative',
          }}>
            One thread for chat, image, video, and music - with a memory
            layer that carries context between every turn. The composer waits
            for you at the bottom of the page.
          </p>

          {/* Modality folio strip - Unique professional pills */}
          <div className="bento-modality-strip" style={{
            marginTop: isCompact ? 14 : 32, paddingTop: isCompact ? 10 : 20,
            borderTop: `1px solid ${ATELIER.borderSoft}`,
            display: 'flex', alignItems: 'center',
            gap: 12,
            position: 'relative',
          }}>
            {['chat', 'image', 'video', 'music'].map((mId, i) => {
              const mDef = MODES.find(m => m.id === mId);
              const tint = ATELIER.modes[mId]?.accent || ATELIER.accent;
              return (
                <motion.div key={mId}
                  whileHover={{ scale: 1.05, y: -2 }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: isCompact ? '5px 12px' : '8px 16px',
                    borderRadius: 999,
                    background: `linear-gradient(135deg, #ffffff 0%, ${ATELIER.bgSubtle} 100%)`,
                    border: `1px solid ${ATELIER.border}`,
                    boxShadow: '0 2px 8px rgba(10,10,20,0.04), inset 0 1px 0 rgba(255,255,255,0.8)',
                    cursor: 'default',
                  }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: 12,
                    background: `${tint}15`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                      stroke={tint} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d={ICON[mId]} />
                    </svg>
                  </div>
                  <span style={{
                    fontFamily: FONTS.sans, fontSize: 13, fontWeight: 600,
                    color: ATELIER.ink, letterSpacing: '-0.01em',
                  }}>
                    {mDef?.label || mId}
                  </span>
                </motion.div>
              );
            })}
            <span style={{ flex: 1 }} />
            <span className="bento-modality-strip-text" style={{
              fontFamily: FONTS.mono, fontSize: 9.5, fontWeight: 700,
              letterSpacing: '0.22em', color: ATELIER.inkFaint,
              textTransform: 'uppercase',
            }}>
              Four modalities, one thread
            </span>
          </div>
        </motion.div>

        {/* TILE ROW - Latest work + Memory + Active model */}
        <div className="bento-tile-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
          gridAutoRows: isCompact ? '150px' : '200px',
          gap: isCompact ? 12 : 20,
          marginBottom: isCompact ? 12 : 20,
        }}>
          <LatestWorkTile visuals={visuals} index={0} />
          <StorageTile turns={allTurns} index={1} />
          <ProvidersTile index={2} />
        </div>

        {/* PASSPORT STRIP - dark, full width */}
        <PassportStrip dateShort={dateShort} turns={allTurns} index={3} dgccObj={dgccBalance} />

        {/* Bottom editorial signoff - fleuron + Roman year */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          style={{
            marginTop: isCompact ? 12 : 28,
            display: 'flex', alignItems: 'center', gap: 14,
          }}
        >
          <span style={{ flex: 1, height: 1, background: ATELIER.borderSoft }} />
          <span style={{
            fontFamily: FONTS.serif, fontSize: 18,
            color: ATELIER.accent, fontWeight: 400,
            lineHeight: 1, letterSpacing: 0,
          }} aria-hidden>❦</span>
          <span style={{
            fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.26em',
            color: ATELIER.inkFaint, fontWeight: 600, textTransform: 'uppercase',
          }}>
            Anchored on DAGCHAIN · Est. {romanYear}
          </span>
          <span style={{
            fontFamily: FONTS.serif, fontSize: 18,
            color: ATELIER.accent, fontWeight: 400,
            lineHeight: 1, letterSpacing: 0,
          }} aria-hidden>❦</span>
          <span style={{ flex: 1, height: 1, background: ATELIER.borderSoft }} />
        </motion.div>
      </div>
    </div>
  );
}

/* Removed ChapterLabel */

/* ═════════════════════════════════════════════════════════════
   ░  REGISTER MARKS - Swiss-print style corner crosshairs        ░
   ═════════════════════════════════════════════════════════════ */
function RegisterMark({ position = 'tl' }) {
  const size = 14;
  const offset = 14;
  const pos = {
    tl: { top: offset, left: offset },
    tr: { top: offset, right: offset, transform: 'rotate(90deg)' },
    bl: { bottom: offset, left: offset, transform: 'rotate(-90deg)' },
    br: { bottom: offset, right: offset, transform: 'rotate(180deg)' },
  }[position];
  return (
    <div aria-hidden style={{
      position: 'absolute', width: size, height: size,
      pointerEvents: 'none',
      ...pos,
    }}>
      <div style={{
        position: 'absolute', top: 0, left: 0,
        width: '100%', height: 1,
        background: ATELIER.borderStrong,
      }} />
      <div style={{
        position: 'absolute', top: 0, left: 0,
        width: 1, height: '100%',
        background: ATELIER.borderStrong,
      }} />
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════ */
/*  TILES                                                         */
/* ═════════════════════════════════════════════════════════════ */

/* ── Latest work tile (6�-2) ─────────────────────────────── */
function getTurnAspectRatio(turn) {
  const meta = turn.meta || {};
  const size = meta.imageSize || meta.aspectRatio || meta.aspect_ratio || '';
  const str = String(size).toLowerCase();
  
  if (str.includes('16:9') || str.includes('16_9') || str.includes('landscape') || str.includes('3:2') || str.includes('3_2')) {
    return '16 / 9';
  }
  if (str.includes('9:16') || str.includes('9_16') || str.includes('portrait') || str.includes('2:3') || str.includes('2_3')) {
    return '9 / 16';
  }
  return '1 / 1';
}

/* ── Latest work tile (6-2) ─────────────────────────────── */
function LatestWorkTile({ visuals, index }) {
  return (
    <BentoTile
      colSpan={6}
      rowSpan={2}
      index={index}
      label="Latest work"
      cornerAction={
        visuals.length > 0 && (
          <span style={{
            fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.2em',
            color: ATELIER.inkMuted, fontWeight: 600, textTransform: 'uppercase',
          }}>
            {visuals.length} item{visuals.length > 1 ? 's' : ''}
          </span>
        )
      }
    >
      {visuals.length === 0 ? (
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ textAlign: 'center', maxWidth: 280 }}>
            <div style={{
              fontFamily: FONTS.serif, fontSize: 26, fontStyle: 'italic',
              color: ATELIER.inkSoft, margin: '0 0 8px', letterSpacing: '-0.025em',
              lineHeight: 1.15,
            }}>
              Your gallery<br />begins empty.
            </div>
            <div style={{
              fontFamily: FONTS.mono, fontSize: 10, letterSpacing: '0.22em',
              color: ATELIER.inkMuted, fontWeight: 600, textTransform: 'uppercase',
              marginTop: 10,
            }}>
              Images · Videos · Music
            </div>
          </div>
        </div>
      ) : (
        <div 
          className="custom-scrollbar"
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 12,
            width: '100%',
            height: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            paddingBottom: 8,
          }}
        >
          {visuals.map(t => (
            <VisualTile key={t.id} turn={t} />
          ))}
        </div>
      )}
    </BentoTile>
  );
}

function VisualTile({ turn }) {
  const { setExpandedMedia } = useWorkspace();
  let url = turn.imageUrl || turn.videoThumb || null;
  if (turn.type === 'music' && turn.meta?.sunoTracks?.length > 0) {
    url = turn.meta.sunoTracks[0].image_url || turn.meta.sunoTracks[0].imageUrl || url;
  }
  const label = turn.type === 'video' ? 'video' : turn.type === 'music' ? 'music' : 'image';
  const ratio = getTurnAspectRatio(turn);

  const handleClick = () => {
    if (turn.type === 'video' && turn.videoUrl) {
      setExpandedMedia({ url: turn.videoUrl, type: 'video', title: turn.text || 'Generated Video' });
    } else if (turn.type === 'image' && turn.imageUrl) {
      setExpandedMedia({ url: turn.imageUrl, type: 'image', title: turn.text || 'Generated Image' });
    } else if (turn.type === 'music' && turn.audioUrl) {
      setExpandedMedia({ url: turn.audioUrl, type: 'audio', title: turn.text || 'Generated Music' });
    }
  };

  return (
    <div 
      onClick={handleClick}
      style={{
        position: 'relative',
        borderRadius: 10,
        overflow: 'hidden',
        height: '100%',
        aspectRatio: ratio,
        flexShrink: 0,
        background: url
          ? `url(${url}) center / cover`
          : 'linear-gradient(135deg, #C4B5FD 0%, #A78BFA 100%)',
        cursor: 'pointer',
        transition: 'transform 0.25s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'scale(1.02)';
        const video = e.currentTarget.querySelector('video');
        if (video) video.play().catch(() => {});
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'scale(1)';
        const video = e.currentTarget.querySelector('video');
        if (video) video.pause();
      }}
    >
      {turn.type === 'video' && turn.videoUrl && (
        <video
          src={turn.videoUrl}
          muted
          loop
          preload="auto"
          playsInline
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            pointerEvents: 'none',
          }}
        />
      )}

      <div aria-hidden style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, height: '50%',
        background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)',
        pointerEvents: 'none',
      }} />
      <span style={{
        position: 'absolute', left: 8, bottom: 6,
        fontFamily: FONTS.mono, fontSize: 9, letterSpacing: '0.22em',
        color: 'rgba(255,255,255,0.92)', fontWeight: 700,
        textTransform: 'uppercase',
        pointerEvents: 'none',
      }}>{label}</span>
      {turn.type === 'video' && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 28, height: 28, borderRadius: 14,
          background: 'rgba(255,255,255,0.92)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          pointerEvents: 'none',
        }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill={ATELIER.ink}>
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      )}
    </div>
  );
}

/* ── Storage tile (3x2) ─────────────────────────────────────
   Shows total storage used + a stacked breakdown bar + per-type
   rows with bytes. */
function StorageTile({ turns, index }) {
  const { byType, total } = computeStorage(turns);
  const headline = formatStorage(total);
  const pct = Math.min(100, (total / STORAGE_QUOTA_MB) * 100);

  // Order largest -> smallest for the breakdown
  const breakdown = Object.entries(byType)
    .filter(([, mb]) => mb > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <BentoTile
      colSpan={3}
      rowSpan={2}
      index={index}
      label="Storage"
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Headline number */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
          <span style={{
            fontFamily: FONTS.serif, fontSize: 44, fontWeight: 400,
            lineHeight: 1, letterSpacing: '-0.03em',
            color: ATELIER.ink,
            fontVariantNumeric: 'tabular-nums',
          }}>{headline.value}</span>
          <span style={{
            fontFamily: FONTS.mono, fontSize: 11, letterSpacing: '0.16em',
            color: ATELIER.inkMid, fontWeight: 700, textTransform: 'uppercase',
          }}>{headline.unit}</span>
        </div>
        <div style={{
          fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.22em',
          color: ATELIER.inkMuted, fontWeight: 600, textTransform: 'uppercase',
          marginBottom: 14,
        }}>
          Total usage
        </div>

        {/* Stacked usage bar */}
        <div style={{
          height: 8, borderRadius: 4,
          background: ATELIER.bgHover,
          overflow: 'hidden', display: 'flex',
          marginBottom: 14,
        }}>
          {total > 0 ? Object.entries(byType).map(([k, mb]) => {
            if (mb <= 0) return null;
            const w = (mb / STORAGE_QUOTA_MB) * 100;
            return (
              <div key={k} title={`${STORAGE_TYPE_META[k]?.label}: ${formatStorage(mb).value} ${formatStorage(mb).unit}`}
                style={{
                  width: `${w}%`,
                  background: STORAGE_TYPE_META[k]?.accent || ATELIER.accent,
                }} />
            );
          }) : null}
        </div>

        {/* Per-type breakdown rows */}
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column', gap: 8,
          fontFamily: FONTS.sans,
        }}>
          {breakdown.length === 0 ? (
            <div style={{
              fontFamily: FONTS.serif, fontSize: 17, fontStyle: 'italic',
              color: ATELIER.inkSoft, letterSpacing: '-0.018em', lineHeight: 1.3,
              marginTop: 4,
            }}>
              Nothing stored yet.
              <div style={{
                marginTop: 6,
                fontFamily: FONTS.mono, fontSize: 9, letterSpacing: '0.22em',
                color: ATELIER.inkMuted, fontWeight: 600, textTransform: 'uppercase',
                fontStyle: 'normal',
              }}>
                First output writes here
              </div>
            </div>
          ) : breakdown.slice(0, 4).map(([k, mb]) => {
            const meta = STORAGE_TYPE_META[k];
            const fmt = formatStorage(mb);
            return (
              <div key={k} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 12.5,
              }}>
                <span style={{
                  width: 7, height: 7, borderRadius: 2,
                  background: meta.accent, flexShrink: 0,
                }} />
                <span style={{
                  flex: 1, color: ATELIER.inkMid, fontWeight: 500,
                  letterSpacing: '-0.005em',
                }}>{meta.label}</span>
                <span style={{
                  fontFamily: FONTS.mono, fontSize: 11,
                  color: ATELIER.ink, fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                }}>{fmt.value}</span>
                <span style={{
                  fontFamily: FONTS.mono, fontSize: 9, letterSpacing: '0.14em',
                  color: ATELIER.inkMuted, fontWeight: 700, textTransform: 'uppercase',
                  width: 18, textAlign: 'right',
                }}>{fmt.unit}</span>
              </div>
            );
          })}
        </div>
      </div>
    </BentoTile>
  );
}

/* ── Provider logo map ──────────────────────────────────────
   Source of truth shared with the chatbox / ModelsPage. */
const PROVIDER_LOGOS = {
  Anthropic: '/claude-ai-icon.svg',
  OpenAI:    '/images/logos/openai.svg',
  Google:    '/google-icon.svg',
  Meta:      '/meta-icon.svg',
  xAI:       '/xai-icon.svg',
  Mistral:   '/mistral-icon.svg',
  Cohere:    '/images/logos/cohere.svg',
  DeepSeek:  '/deepseek-logo-icon.svg',
  Qwen:      '/qwen-ai-icon.svg',
  Microsoft: '/microsoft-icon.svg',
  Perplexity:'/images/logos/perplexity.svg',
  AI21:      '/ai21-icon.svg',
  ByteDance: '/bytedance.svg',
  'Black Forest': '/flux.svg',
  'Black Forest Labs': '/flux.svg',
  Runway:    '/runway.svg',
  Kuaishou:  '/kling-icon.svg',
  Kling:     '/kling-icon.svg',
  Ideogram:  '/ideogram.svg',
  Stability: '/images/logos/openai.svg', // fallback
  Suno:      '/suno.svg',
  Udio:      '/suno.svg',                // fallback to a music icon
};

/* ── Providers tile (3x2) ───────────────────────────────────
   Logo grid of every model provider available across all
   modalities, with model counts. Replaces the old single-model
   ActiveModel tile. */
function ProvidersTile({ index }) {
  const { availableModels } = useWorkspace();

  // Aggregate provider -> model count across all modes
  const providerCounts = useMemo(() => {
    const counts = new Map();
    const allLists = availableModels
      ? [...(availableModels.chat || []), ...(availableModels.image || []),
         ...(availableModels.video || []), ...(availableModels.music || [])]
      : [];
    for (const m of allLists) {
      const p = m.provider || (m.id || '').split('/')[0] || 'Other';
      const norm = p.charAt(0).toUpperCase() + p.slice(1);
      counts.set(norm, (counts.get(norm) || 0) + 1);
    }
    // Filter out vendors
    const filteredCounts = Array.from(counts.entries())
      .filter(([name]) => {
        const lower = name.toLowerCase();
        return !lower.includes('kie') && !lower.includes('openrouter') && !lower.includes('open router');
      });
    // Stable sort: known providers first (highest count), unknowns last
    return filteredCounts.sort((a, b) => b[1] - a[1]);
  }, [availableModels]);

  const totalModels = providerCounts.reduce((a, [, n]) => a + n, 0);
  const totalProviders = providerCounts.length;

  return (
    <BentoTile
      colSpan={3}
      rowSpan={2}
      index={index}
      label="Providers"
      cornerAction={
        <span style={{
          fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.2em',
          color: ATELIER.inkMuted, fontWeight: 700, textTransform: 'uppercase',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {totalProviders.toString().padStart(2, '0')} · {totalModels} models
        </span>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <div 
          className="custom-scrollbar"
          style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gridAutoRows: 'max-content',
          gap: 6,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          paddingRight: 4,
        }}>
          {providerCounts.map(([name, count]) => (
            <ProviderChip key={name} name={name} count={count} />
          ))}
        </div>

        <div style={{
          marginTop: 10, paddingTop: 10,
          borderTop: `1px solid ${ATELIER.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: 7,
          fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.2em',
          color: ATELIER.inkMuted, fontWeight: 700, textTransform: 'uppercase',
        }}>
          <span style={{
            width: 5, height: 5, borderRadius: 3,
            background: ATELIER.ok,
            boxShadow: `0 0 6px ${ATELIER.ok}`,
          }} />
          All providers online
        </div>
      </div>
    </BentoTile>
  );
}

function ProviderChip({ name, count }) {
  const logo = PROVIDER_LOGOS[name];
  const [imgOk, setImgOk] = React.useState(true);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 7,
      padding: '6px 8px',
      background: ATELIER.bgSubtle,
      border: `1px solid ${ATELIER.borderSoft}`,
      borderRadius: 7,
      minWidth: 0,
      transition: 'background 0.15s, border-color 0.15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.background = ATELIER.bgHover; e.currentTarget.style.borderColor = ATELIER.border; }}
      onMouseLeave={e => { e.currentTarget.style.background = ATELIER.bgSubtle; e.currentTarget.style.borderColor = ATELIER.borderSoft; }}
    >
      <span style={{
        width: 16, height: 16, borderRadius: 4,
        background: '#FFFFFF',
        border: `1px solid ${ATELIER.borderSoft}`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
        overflow: 'hidden',
      }}>
        {logo && imgOk ? (
          <img
            src={logo}
            alt=""
            onError={() => setImgOk(false)}
            style={{ width: 11, height: 11, objectFit: 'contain' }}
          />
        ) : (
          <span style={{
            fontFamily: FONTS.mono, fontSize: 8.5, fontWeight: 700,
            color: ATELIER.accent,
          }}>{name.charAt(0)}</span>
        )}
      </span>
      <span style={{
        flex: 1, minWidth: 0,
        fontSize: 11.5, fontWeight: 600,
        color: ATELIER.ink, letterSpacing: '-0.005em',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{name}</span>
      <span style={{
        fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.1em',
        color: ATELIER.inkMuted, fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        flexShrink: 0,
      }}>{count}</span>
    </div>
  );
}

/* ── Projects strip (12 cols) ───────────────────────────── */
function ProjectsStrip({ projects, index }) {
  const { createProject, openProject } = useWorkspace();
  const top = (projects || []).slice(0, 5);

  return (
    <BentoTile
      colSpan={12}
      rowSpan={1}
      index={index}
      label={<ChapterLabel num="04" text={`Projects · ${(projects || []).length}`} />}
      cornerAction={
        <button
          type="button"
          onClick={() => createProject && createProject('Untitled project')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: 'transparent', border: 'none',
            padding: '4px 8px', borderRadius: 6,
            fontFamily: FONTS.mono, fontSize: 10, letterSpacing: '0.18em',
            fontWeight: 700, textTransform: 'uppercase',
            color: ATELIER.accent, cursor: 'pointer',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = ATELIER.accentSoft}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          + New
        </button>
      }
    >
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: 14,
        flex: 1,
        minHeight: 0,
      }}>
        {[0, 1, 2, 3, 4].map(i => {
          const serial = `No. ${String(i + 1).padStart(2, '0')}`;
          const p = top[i];
          return (
            <div key={p?.id || `e${i}`} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{
                fontFamily: FONTS.mono, fontSize: 9, letterSpacing: '0.24em',
                fontWeight: 700, color: ATELIER.inkFaint,
                textTransform: 'uppercase',
                fontVariantNumeric: 'tabular-nums',
                paddingLeft: 2,
              }}>{serial}</span>
              {p
                ? <ProjectCard project={p} onOpen={() => openProject(p.id)} />
                : <ProjectPlaceholder onClick={() => createProject && createProject('Untitled project')} />}
            </div>
          );
        })}
      </div>

      {/* Shelf rule */}
      <div aria-hidden style={{
        marginTop: 10,
        height: 1,
        background: `linear-gradient(90deg, transparent, ${ATELIER.borderSoft} 8%, ${ATELIER.borderSoft} 92%, transparent)`,
      }} />
    </BentoTile>
  );
}

function ProjectCard({ project, onOpen }) {
  const seed = hashStr(project.title || project.id || '');
  const pairs = [
    ['#7C3AED', '#4F46E5'], ['#5B21B6', '#7C3AED'], ['#6D28D9', '#A78BFA'],
    ['#4338CA', '#7C3AED'], ['#9333EA', '#5B21B6'], ['#A855F7', '#6366F1'],
    ['#6E3FE3', '#EC4899'], ['#4F46E5', '#9333EA'], ['#8B5CF6', '#3730A3'],
  ];
  const [a, b] = pairs[Math.abs(seed) % pairs.length];
  const angle = 110 + (seed % 60);
  const initial = (project.title || '?').trim().charAt(0).toUpperCase();

  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        position: 'relative',
        border: 'none', padding: 0,
        borderRadius: 14,
        overflow: 'hidden',
        cursor: 'pointer',
        height: '100%',
        minHeight: 120,
        background: `linear-gradient(${angle}deg, ${a} 0%, ${b} 100%)`,
        transition: 'transform 0.22s, box-shadow 0.22s',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 24px rgba(124,58,237,0.28)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Wax-seal highlight */}
      <div aria-hidden style={{
        position: 'absolute', left: 0, right: 0, top: 0, height: '55%',
        background: 'radial-gradient(ellipse 90% 80% at 30% 0%, rgba(255,255,255,0.42) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      {/* Serif initial */}
      <div style={{
        position: 'absolute', top: 12, left: 12,
        fontFamily: FONTS.serif, fontSize: 32, fontWeight: 500,
        letterSpacing: '-0.03em', color: '#FFFFFF',
        textShadow: '0 1px 3px rgba(0,0,0,0.24)',
        lineHeight: 1,
      }}>{initial}</div>
      {/* Title bottom */}
      <div style={{
        position: 'absolute', left: 12, right: 12, bottom: 10,
        textAlign: 'left',
      }}>
        <div style={{
          fontFamily: FONTS.sans, fontSize: 13, fontWeight: 600,
          color: '#FFFFFF', letterSpacing: '-0.005em',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          textShadow: '0 1px 3px rgba(0,0,0,0.30)',
        }}>{project.title}</div>
        <div style={{
          marginTop: 2,
          fontFamily: FONTS.mono, fontSize: 9, letterSpacing: '0.18em',
          color: 'rgba(255,255,255,0.72)', fontWeight: 600,
          textTransform: 'uppercase',
        }}>
          {project.starred ? '★ Starred' : relativeTime(project.updatedAt)}
        </div>
      </div>
    </button>
  );
}

function ProjectPlaceholder({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        position: 'relative',
        border: `1px dashed ${ATELIER.borderStrong}`,
        padding: 0,
        borderRadius: 14,
        background: ATELIER.bgHover,
        cursor: 'pointer',
        minHeight: 120,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 8,
        color: ATELIER.inkMuted,
        transition: 'background 0.15s, border-color 0.15s, color 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = ATELIER.accentSoft; e.currentTarget.style.borderColor = ATELIER.borderAccent; e.currentTarget.style.color = ATELIER.accent; }}
      onMouseLeave={e => { e.currentTarget.style.background = ATELIER.bgHover; e.currentTarget.style.borderColor = ATELIER.borderStrong; e.currentTarget.style.color = ATELIER.inkMuted; }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d={ICON.plus} />
      </svg>
      <span style={{
        fontFamily: FONTS.mono, fontSize: 9, letterSpacing: '0.22em',
        fontWeight: 700, textTransform: 'uppercase',
      }}>New</span>
    </button>
  );
}

/* ── Passport strip (dark, 12 cols) ───────────────────────
   Five-zone layout (no overlaps):
   [DGCC] | [DGCC trend] || [STORAGE] | [STORAGE trend] || [Anchored] [Streak] | [Seal] */
function PassportStrip({ dateShort, turns, index, dgccObj }) {
  const isCompact = window.innerWidth < 1440 || window.innerHeight < 850;
  // ANCHORED
  const anchored = turns.filter(t => t.role === 'assistant' && t.status === 'done').length;

  // STREAK
  const streak = (() => {
    const days = new Set(
      turns.filter(t => t.createdAt).map(t => new Date(t.createdAt).toDateString())
    );
    let s = 0;
    const d = new Date();
    while (days.has(d.toDateString())) { s += 1; d.setDate(d.getDate() - 1); }
    return s;
  })();

  const balance = dgccObj?.balance ?? 0;
  const totalPurchased = dgccObj?.total_purchased ?? 0;
  const totalBonus = dgccObj?.total_bonus ?? 0;
  const totalGot = (totalPurchased + totalBonus) || balance;

  const dgcc = balance;

  // DGCC sparkline
  const sparkPts = [2, 6, 4, 9, 7, 12, 10, 14, 11, 16, 13, 18, 15, 20, 17, 22];
  const SP_W = 160, SP_H = 32;
  const sparkMax = Math.max(...sparkPts);
  const sparkPath = sparkPts.map((y, i) => {
    const x = (i / (sparkPts.length - 1)) * SP_W;
    const yy = SP_H - (y / sparkMax) * SP_H;
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${yy.toFixed(1)}`;
  }).join(' ');

  // STORAGE
  const storage = computeStorage(turns);
  const totalFmt = formatStorage(storage.total);
  const monthFmt = formatStorage(storage.monthMB);

  // Storage 12-week bar trend (synthesised proportional to total + month)
  const barSeed = [3, 5, 4, 7, 6, 9, 8, 11, 10, 14, 12, 17];
  const barMax = Math.max(...barSeed);

  return (
    <BentoTile
      colSpan={12}
      rowSpan={1}
      variant="dark"
      index={index}
      padded={false}
    >
      {/* Register marks */}
      <DarkRegisterMark position="tl" />
      <DarkRegisterMark position="tr" />
      <DarkRegisterMark position="bl" />
      <DarkRegisterMark position="br" />

      <div style={{
        padding: isCompact ? '10px 20px' : '20px 28px',
        display: 'grid',
        gridTemplateColumns: 'auto auto 1fr auto auto auto auto',
        gap: isCompact ? 16 : 24,
        alignItems: 'center',
        height: '100%',
      }}>
        {/* ── ZONE 1 · DGCC Balance ──────────────────── */}
        <div>
          <DarkLabel>DGCC Balance</DarkLabel>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4 }}>
            <span style={{
              fontFamily: FONTS.serif,
              fontSize: isCompact ? 28 : 40, fontWeight: 300, lineHeight: 0.95,
              letterSpacing: '-0.03em', color: '#FFFFFF',
              fontVariantNumeric: 'tabular-nums',
            }}>{dgcc.toLocaleString()}</span>

          </div>
        </div>

        {/* ── ZONE 2 · DGCC trend ────────────────────── */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
          paddingLeft: 16,
          borderLeft: '1px solid rgba(255,255,255,0.08)',
        }}>
          <DarkLabel>16-day trend</DarkLabel>
          <svg width={SP_W} height={SP_H} viewBox={`0 0 ${SP_W} ${SP_H}`}
            style={{ display: 'block', marginTop: 6 }}>
            <defs>
              <linearGradient id="sparkFillWide" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#A78BFA" stopOpacity="0.38" />
                <stop offset="100%" stopColor="#A78BFA" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={`${sparkPath} L${SP_W},${SP_H} L0,${SP_H} Z`} fill="url(#sparkFillWide)" />
            <path d={sparkPath} fill="none" stroke="#A78BFA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {/* ── ZONE 3 · Storage block (label + numbers + bar trend) ───── */}
        <div style={{
          paddingLeft: 16,
          borderLeft: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', alignItems: 'center', gap: 18,
          minWidth: 0,
        }}>
          <div>
            <DarkLabel>Storage</DarkLabel>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
              <span style={{
                fontFamily: FONTS.serif,
                fontSize: 32, fontWeight: 300, lineHeight: 0.95,
                letterSpacing: '-0.025em', color: '#FFFFFF',
                fontVariantNumeric: 'tabular-nums',
              }}>{totalFmt.value}</span>
              <span style={{
                fontFamily: FONTS.mono, fontSize: 10, letterSpacing: '0.18em',
                color: 'rgba(255,255,255,0.7)', fontWeight: 700,
                textTransform: 'uppercase',
              }}>{totalFmt.unit}</span>
            </div>
            <div style={{
              marginTop: 4,
              fontFamily: FONTS.mono, fontSize: 9, letterSpacing: '0.18em',
              color: '#86EFAC', fontWeight: 700, textTransform: 'uppercase',
              fontVariantNumeric: 'tabular-nums',
            }}>
              + {monthFmt.value} {monthFmt.unit} this month
            </div>
          </div>

          {/* Bar trend (12 weeks) */}
          <div style={{
            display: 'flex', alignItems: 'flex-end', gap: 3,
            height: 38,
            flex: '0 0 auto',
          }}>
            {barSeed.map((v, i) => {
              const h = (v / barMax) * 100;
              const isLast = i === barSeed.length - 1;
              return (
                <div key={i} style={{
                  width: 6, height: `${h}%`,
                  borderRadius: 1.5,
                  background: isLast
                    ? 'linear-gradient(180deg, #A78BFA, #7C3AED)'
                    : 'linear-gradient(180deg, rgba(167,139,250,0.45), rgba(167,139,250,0.20))',
                  boxShadow: isLast ? '0 0 8px rgba(167,139,250,0.55)' : 'none',
                }} />
              );
            })}
          </div>
        </div>

        {/* ── ZONE 4 · Anchored ──────────────────────── */}
        <div style={{
          paddingLeft: 18,
          borderLeft: '1px solid rgba(255,255,255,0.08)',
        }}>
          <Stat
            label="Anchored"
            value={String(anchored).padStart(2, '0')}
            hint={anchored === 1 ? 'sealed' : 'sealed'}
          />
        </div>

        {/* ── ZONE 5 · Streak ────────────────────────── */}
        <div>
          <Stat
            label="Streak"
            value={String(streak).padStart(2, '0')}
            hint={streak === 1 ? 'day' : 'days'}
            streak={streak >= 3}
          />
        </div>

        {/* ── ZONE 6 · Anchor seal ───────────────────── */}
        <AnchorSeal />

        {/* ── ZONE 7 · Issued date ───────────────────── */}
        <div style={{
          padding: '6px 10px',
          borderRadius: 7,
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.08)',
          fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.2em',
          color: 'rgba(255,255,255,0.65)', fontWeight: 700,
          textTransform: 'uppercase',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {dateShort}
        </div>
      </div>
    </BentoTile>
  );
}

function DarkLabel({ children }) {
  return (
    <div style={{
      fontFamily: FONTS.mono, fontSize: 9.5, letterSpacing: '0.24em',
      color: 'rgba(255,255,255,0.45)', fontWeight: 700,
      textTransform: 'uppercase',
    }}>{children}</div>
  );
}

/* Dark register mark (for the passport strip) */
function DarkRegisterMark({ position = 'tl' }) {
  const size = 12;
  const offset = 10;
  const pos = {
    tl: { top: offset, left: offset },
    tr: { top: offset, right: offset },
    bl: { bottom: offset, left: offset },
    br: { bottom: offset, right: offset },
  }[position];
  return (
    <div aria-hidden style={{
      position: 'absolute', width: size, height: size,
      pointerEvents: 'none',
      ...pos,
    }}>
      <div style={{
        position: 'absolute', top: 0, left: 0,
        width: '100%', height: 1,
        background: 'rgba(255,255,255,0.18)',
      }} />
      <div style={{
        position: 'absolute', top: 0, left: 0,
        width: 1, height: '100%',
        background: 'rgba(255,255,255,0.18)',
      }} />
    </div>
  );
}

/* Circular seal-style badge for the passport */
function AnchorSeal() {
  return (
    <div style={{
      position: 'relative',
      width: 64, height: 64,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {/* Outer ring with curved text */}
      <svg width="64" height="64" viewBox="0 0 64 64" style={{ position: 'absolute', inset: 0 }}>
        <defs>
          <path id="sealArcTop" d="M 32 32 m -24, 0 a 24 24 0 1 1 48 0" fill="none" />
          <path id="sealArcBot" d="M 32 32 m 24, 0 a 24 24 0 1 1 -48 0" fill="none" />
        </defs>
        <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(167,139,250,0.32)" strokeWidth="1" />
        <circle cx="32" cy="32" r="22" fill="none" stroke="rgba(167,139,250,0.22)" strokeWidth="1" strokeDasharray="1,3" />
        <text fontSize="6.5" fontFamily="ui-monospace, SFMono-Regular, monospace"
          fontWeight="700" letterSpacing="2.5" fill="rgba(255,255,255,0.55)">
          <textPath href="#sealArcTop" startOffset="16%">ANCHORED · DAGCHAIN</textPath>
        </text>
        <text fontSize="6.5" fontFamily="ui-monospace, SFMono-Regular, monospace"
          fontWeight="700" letterSpacing="2.5" fill="rgba(255,255,255,0.35)">
          <textPath href="#sealArcBot" startOffset="14%">VERIFIED · SEALED</textPath>
        </text>
      </svg>
      {/* Inner glyph - interlocking rings */}
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke="#A78BFA" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
        style={{ position: 'relative', zIndex: 1 }}>
        <circle cx="9" cy="12" r="4" />
        <circle cx="15" cy="12" r="4" />
      </svg>
    </div>
  );
}

function Stat({ label, value, hint, streak }) {
  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 6,
        marginBottom: 2,
      }}>
        <div style={{
          fontFamily: FONTS.serif,
          fontSize: 28, fontWeight: 300,
          color: '#FFFFFF', letterSpacing: '-0.02em',
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
        }}>{value}</div>
        {streak && (
          <span aria-hidden style={{
            fontSize: 13, lineHeight: 1,
            filter: 'saturate(1.2)',
          }}>🔥</span>
        )}
      </div>
      <div style={{
        fontFamily: FONTS.mono,
        fontSize: 9.5, letterSpacing: '0.22em', fontWeight: 700,
        color: streak ? '#FBBF24' : 'rgba(255,255,255,0.55)',
        textTransform: 'uppercase',
        marginBottom: hint ? 2 : 0,
      }}>{label}</div>
      {hint && (
        <div style={{
          fontFamily: FONTS.mono,
          fontSize: 8.5, letterSpacing: '0.18em', fontWeight: 600,
          color: 'rgba(255,255,255,0.30)', textTransform: 'uppercase',
        }}>{hint}</div>
      )}
    </div>
  );
}

/* ═ Helpers ══════════════════════════════════════════════ */
function hashStr(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) { h = (h << 5) - h + s.charCodeAt(i); h |= 0; }
  return h;
}

function relativeTime(ts) {
  if (!ts) return 'New';
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1)   return 'Just now';
  if (min < 60)  return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24)   return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30)  return `${day}d ago`;
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
